import { Button, Icon, notify } from '@yembo/yemblocks-core';
import { useState } from 'react';
import { convertHeicToJpg } from 'src/react/_util/convertHeicToJpg';

const IMAGE_MAX_SIZE = 1000000000; // bytes
const ACCEPTED_FILE_TYPES = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'image/avif',
];
const INPUT_ALLOWED_FILE_TYPES = ACCEPTED_FILE_TYPES.map((type) =>
  ['image/heic', 'image/heif'].includes(type) ? `.${type.split('/').pop()}` : type
);

type Props = {
  onUpload: (file: File) => void;
  setIsImageLoading: (value: boolean) => void;
};

export const UploadImagesButton = ({ onUpload, setIsImageLoading }: Props) => {
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const verifyFile = (file: File) => {
    const currentFileType = file.type;
    const currentFileSize = file.size;
    if (currentFileSize > IMAGE_MAX_SIZE) {
      notify.error(`This file is not allowed. ${currentFileSize} bytes is too large`);
      return false;
    }

    if (!ACCEPTED_FILE_TYPES.includes(currentFileType)) {
      notify.error('This file is not allowed. Only images with jpg/png/heic/heif/webp/avif extensions are allowed.');
      return false;
    }

    return true;
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsImageLoading(true);
    const { files } = event.currentTarget;
    let file = files?.[0];
    if (!file) return;

    if (['image/heif', 'image/heic', ''].includes(file.type)) {
      try {
        file = await convertHeicToJpg(file);
      } catch {
        notify.error(`Unable to upload file ${file.name}. Please try again or use a different format.`);
        setIsImageLoading(false);
        return;
      }
    }

    const isVerified = verifyFile(file);
    if (isVerified) {
      onUpload(file);
    }
    setIsImageLoading(false);
  };

  return (
    <>
      <Button kind='flat' text='Upload Images' icon={<Icon name='upload' />} onClick={() => inputRef?.click()} />
      <input
        hidden
        type='file'
        accept={INPUT_ALLOWED_FILE_TYPES.join(',')}
        className='file-input'
        ref={setInputRef}
        onChange={onFileChange}
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      />
    </>
  );
};
